import React, { useEffect, useState, useMemo, useCallback } from "react";
import { db, auth, storage } from "../utils/firebase.js";
import { Navigate, useNavigate } from "react-router-dom";
import { Timestamp } from "firebase/firestore";
import { getDownloadURL, ref, uploadBytes } from "firebase/storage";
import {
  addDoc,
  updateDoc,
  doc,
  collection,
  getDocs,
  getDoc,
  deleteDoc,
  orderBy,
  serverTimestamp,
  setDoc,
  query,
  writeBatch,
  where,
} from "firebase/firestore";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Spinner } from "../components";
import NotificationModal from "../components/Modals/NotificationModal.jsx";

import {
  BellPlus,
  FilePlus2,
  Save,
  ChevronDown,
  ChevronRight,
  Plus,
  Info,
  Trash2,
  ClipboardList,
  User,
  Users,
  Percent,
  CircleDollarSign,
  Home,
  TrendingUp,
  TrendingDown,
  Clock,
  Wallet,
} from "lucide-react";
import { Helmet } from "react-helmet";
import { useAuth } from "../hooks/AuthenticationProvider";

const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
};

const getCostByFilter = (bill, filter) => {
  switch (filter.toLowerCase()) {
    case "weekly":
      return bill.weeklyCost || 0;
    case "monthly":
      return bill.monthlyCost || 0;
    case "yearly":
      return bill.yearlyCost || 0;
    default:
      return 0;
  }
};

const CustomTooltip = ({ active, payload, label, housesData }) => {
  if (active && payload && payload.length) {
    // Combine static mappings with dynamically created house mappings
    const dynamicMappings = housesData
      ? Object.keys(housesData).reduce((acc, houseName) => {
          acc[`bills.${houseName}`] = `${houseName} Bills`;
          return acc;
        }, {})
      : {};

    const staticMappings = {
      grossIncome: "Gross Income",
      netIncome: "Net Income",
      incomeLessBills: "Income Less Bills",
    };

    // Merge static and dynamic mappings
    const keyMappings = { ...staticMappings, ...dynamicMappings };

    // Format payload data using the mappings
    const data = payload.map((item) => ({
      label: keyMappings[item.dataKey] || item.dataKey, // Use mapping or fallback to the raw data key
      value: new Intl.NumberFormat("en-US", {
        style: "decimal",
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(item.value),
    }));

    return (
      <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-lg p-4 text-slate-300">
        <p className="text-purple-400 font-semibold mb-2">{`Timestamp: ${label}`}</p>
        <div className="space-y-1">
          {data.map((item) => (
            <p key={item.label} className="text-slate-300">
              <span className="font-medium text-purple-400">{item.label}:</span>{" "}
              {item.value}
            </p>
          ))}
        </div>
      </div>
    );
  }

  return null;
};

const TAX_RATE = 0.14;
const WEEKS_PER_YEAR = 52;
const MONTHS_PER_YEAR = 12;
const WEEKS_PER_MONTH = 4.33;

const BillSection = React.memo(
  ({
    title,
    isOpen,
    toggle,
    bills,
    getBillTotal,
    selectedIncomeFilter,
    onEditBill,
    icon: Icon,
    accentColor = "red",
  }) => (
    <div className="flex flex-col w-full relative">
      <button
        onClick={toggle}
        className="group relative w-full text-left focus:outline-none"
      >
        <div
          className={`${
            isOpen ? "rounded-t-xl" : "rounded-xl"
          } relative bg-slate-800/90 p-4 flex justify-between items-center transition-all duration-300 border border-slate-700 hover:scale-[1.02] hover:shadow-2xl hover:bg-slate-700/90`}
        >
          <div className="flex items-center gap-3">
            <div
              className={`p-2 bg-${accentColor}-500/10 rounded-full flex items-center justify-center shadow-inner group-hover:scale-110 transition-transform duration-300`}
            >
              <Icon
                className={`text-${accentColor}-400 group-hover:text-${accentColor}-300 transition-colors duration-300`}
                size={20}
              />
            </div>
            <h2 className="text-lg font-medium text-slate-100 group-hover:text-slate-50 transition-colors duration-300">
              {title}
            </h2>
          </div>
          <div
            className={`text-${accentColor}-400 group-hover:text-${accentColor}-300 transition-colors duration-300`}
          >
            {isOpen ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
          </div>
        </div>
      </button>
      {isOpen && (
        <div className="absolute top-full w-full bg-slate-900/95 rounded-b-xl border-t border-slate-700 shadow-lg z-50">
          <div className="max-h-[300px] overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800/50">
            <div className="p-4">
              <ul className="space-y-3">
                {bills.map((bill) => (
                  <li
                    key={bill.id}
                    onClick={() => onEditBill(bill)}
                    className="flex justify-between items-center p-3 bg-slate-800/50 rounded-lg transition-all duration-300 cursor-pointer hover:bg-slate-700 hover:shadow-lg hover:scale-[1.02]"
                  >
                    <span className="text-slate-300 group-hover:text-slate-100 transition-colors duration-300">
                      {bill.name}
                    </span>
                    <span className="text-slate-400 group-hover:text-slate-200 transition-colors duration-300">
                      {formatCurrency(
                        getCostByFilter(bill, selectedIncomeFilter)
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
          <div className="p-4 border-t border-slate-700 bg-slate-800/90">
            <div className="flex justify-between items-center">
              <span className="text-slate-400">Total</span>
              <span
                className={`text-xl font-bold text-${accentColor}-400 group-hover:text-${accentColor}-300 transition-colors duration-300`}
              >
                {formatCurrency(getBillTotal(title))}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
);

const Modal = ({ isOpen, onClose, title, children }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 flex items-center justify-center overflow-x-hidden overflow-y-auto z-[100]">
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative w-full max-w-2xl mx-4">
        <div className="relative bg-slate-900/90 backdrop-blur-xl rounded-xl border border-slate-700 shadow-2xl">
          <div className="flex items-center justify-between p-4 border-b border-slate-700">
            <h3 className="text-xl font-semibold text-white">{title}</h3>
            <button
              onClick={onClose}
              className="p-1 text-slate-400 hover:text-slate-200 transition-colors"
            >
              ×
            </button>
          </div>
          <div className="p-6">{children}</div>
        </div>
      </div>
    </div>
  );
};

// Find this part in your code - it's the StatCard component definition
const StatCard = ({
  title,
  value,
  icon: Icon,
  color = "blue",
  isNegative = false,
  tooltip,
  children,
}) => {
  const [isTooltipVisible, setIsTooltipVisible] = useState(false);

  return (
    <div className="relative bg-slate-800/70 backdrop-blur-lg rounded-xl overflow-visible border border-slate-700">
      <div className="relative p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className={`p-2 bg-${color}-500/20 rounded-lg`}>
            <Icon className={`text-${color}-400`} size={24} />
          </div>
          <h3 className="text-lg font-medium text-slate-200 flex items-center gap-2 relative">
            {title}
            {tooltip && (
              <div
                className="relative"
                onMouseEnter={() => setIsTooltipVisible(true)}
                onMouseLeave={() => setIsTooltipVisible(false)}
              >
                <Info
                  className="text-blue-400 hover:text-blue-300 cursor-pointer"
                  size={16}
                />
                {isTooltipVisible && (
                  <div
                    className="absolute bottom-full mb-2 left-1/2 transform -translate-x-[80%] w-72 p-2 bg-gray-300 text-white text-sm rounded-lg shadow-lg z-[999]"
                    style={{
                      whiteSpace: "normal",
                      overflowWrap: "break-word",
                      maxWidth: "100%",
                      minWidth: "200px",
                    }}
                  >
                    <p className="text-gray-900 font-semibold">{tooltip}</p>
                  </div>
                )}
              </div>
            )}
          </h3>
        </div>
        <p
          className={`text-2xl font-bold ${
            isNegative ? `text-${color}-500` : "text-white"
          }`}
        >
          {value}
        </p>
        {children && <div className="mt-4">{children}</div>}
      </div>
    </div>
  );
};

const defaultHouseSection = {};

const Owner = () => {
  const navigate = useNavigate();
  const {
    user,
    isLoading,
    organizationId,
    organizationData: { housesData = {} } = {},
    checkPermission,
  } = useAuth();

  const [openHouseSections, setOpenHouseSections] =
    useState(defaultHouseSection);
  const [snapshots, setSnapshots] = useState([]);
  const [isSaveSnapshotModalOpen, setIsSaveSnapshotModalOpen] = useState(false);
  const [grossIncome, setGrossIncome] = useState(0);
  const [netIncome, setNetIncome] = useState(0);
  const [incomeLessBills, setIncomeLessBills] = useState(0);
  const [hourlyWage, setHourlyWage] = useState(0);
  const [tax, setTax] = useState({ amount: 0, bracket: "" });
  const [selectedIncomeFilter, setSelectedIncomeFilter] = useState("Yearly");
  const [isEditModalOpen, setEditModalOpen] = useState(false);
  const [billToEdit, setBillToEdit] = useState(null);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [residentOwed, setResidentOwed] = useState(0);
  const [invoicesOwed, setInvoicesOwed] = useState(0);
  const [selectedFile, setSelectedFile] = useState(null);
  const [notificationModalOpen, setNotificationModalOpen] = useState(false);
  const [users, setUsers] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [fileName, setFileName] = useState("");
  const [documentModal, setDocumentModal] = useState(false);
  const [isPersonalBillsModalOpen, setPersonalBillsModalOpen] = useState(false);
  const [allBills, setAllBills] = useState([]);
  const [isDeleteModalOpen, setDeleteModalOpen] = useState(false);
  const [billToDelete, setBillToDelete] = useState(null);
  const [addBillModalOpen, setAddBillModalOpen] = useState(false);
  const [taxPercentage, setTaxPercentage] = useState(12);
  const [hoursPerWeek, setHoursPerWeek] = useState(40);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [currentBill, setCurrentBill] = useState({
    type: "Personal",
    name: "",
    cost: "",
    schedule: "Monthly",
  });

  useEffect(() => {
    const initializeHouseSettings = async () => {
      if (!organizationId) return;

      try {
        const settingsRef = doc(
          db,
          `organizations/${organizationId}/houseSettings/initial`
        );
        const settingsSnap = await getDoc(settingsRef);

        if (settingsSnap.exists()) {
          const data = settingsSnap.data();
          const updatedFields = {};

          if (!data.taxPercentage) updatedFields.taxPercentage = 12;
          if (!data.hoursPerWeek) updatedFields.hoursPerWeek = 40;

          if (Object.keys(updatedFields).length > 0) {
            await updateDoc(settingsRef, updatedFields);
          }

          setTaxPercentage(data.taxPercentage || 12);
          setHoursPerWeek(data.hoursPerWeek || 40);
        } else {
          await setDoc(settingsRef, {
            taxPercentage: 12,
            hoursPerWeek: 40,
          });
          setTaxPercentage(12);
          setHoursPerWeek(40);
        }
      } catch (error) {
        console.error("Error initializing house settings:", error);
      }
    };

    initializeHouseSettings();
  }, [organizationId]);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const usersSnapshot = await getDocs(
          collection(db, `organizations/${organizationId}/users`)
        );
        const userList = usersSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setUsers(userList);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    if (organizationId) {
      fetchUsers();
    }
  }, [organizationId]);

  useEffect(() => {
    const debugData = async () => {
      try {
        // Check residents
        const residentsSnapshot = await getDocs(
          query(
            collection(db, `organizations/${organizationId}/residents`),
            where("currentResident", "==", true)
          )
        );
        console.log(
          "Current residents:",
          residentsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        );

        // Check bills
        const billsSnapshot = await getDocs(
          collection(db, `organizations/${organizationId}/billsData`)
        );
        console.log(
          "Bills:",
          billsSnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          }))
        );

        // Check house settings
        const settingsDoc = await getDoc(
          doc(db, `organizations/${organizationId}/houseSettings/initial`)
        );
        console.log(
          "House settings:",
          settingsDoc.exists() ? settingsDoc.data() : "No settings found"
        );
      } catch (error) {
        console.error("Debug error:", error);
      }
    };

    if (organizationId) {
      debugData();
    }
  }, [organizationId]);

  const getCostByFilter = useCallback((bill, filter) => {
    switch (filter.toLowerCase()) {
      case "weekly":
        return bill.weeklyCost || 0;
      case "monthly":
        return bill.monthlyCost || 0;
      case "yearly":
        return bill.yearlyCost || 0;
      default:
        return 0;
    }
  }, []);

  const getBillTotal = useCallback(
    (type, filter = selectedIncomeFilter) => {
      return allBills
        .filter((bill) => bill.type === type)
        .reduce((acc, bill) => acc + getCostByFilter(bill, filter), 0);
    },
    [allBills, selectedIncomeFilter, getCostByFilter]
  );

  const getNetBillTotal = useCallback(
    (filter = selectedIncomeFilter) => {
      return allBills
        .filter((bill) => Object.keys(housesData).includes(bill.type))
        .reduce((acc, bill) => acc + getCostByFilter(bill, filter), 0);
    },
    [allBills, selectedIncomeFilter, getCostByFilter, housesData]
  );

  const fetchBills = useCallback(async () => {
    try {
      const billSnapshot = await getDocs(
        collection(db, `organizations/${organizationId}/billsData`)
      );
      const fetchedBills = billSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllBills(fetchedBills);
    } catch (error) {
      console.error("Error fetching bills:", error);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchBills();
  }, [fetchBills]);

  const calculateIncome = useCallback(async () => {
    if (!organizationId) return;

    try {
      const residentsQuery = query(
        collection(db, `organizations/${organizationId}/residents`),
        where("currentResident", "==", true)
      );
      const residentSnapshot = await getDocs(residentsQuery);

      let totalGrossIncome = 0.0; // Renamed for clarity
      let totalWeeklyIncome = 0.0;
      let totalResidentOwed = 0.0;

      residentSnapshot.forEach((doc) => {
        const { rentSchedule, rentAmount, balance } = doc.data();

        if (parseFloat(balance) < 0) {
          totalResidentOwed += Math.abs(parseFloat(balance));
        }

        const rentAmountFloat = parseFloat(rentAmount);
        const isWeekly = rentSchedule === "weekly";

        switch (selectedIncomeFilter) {
          case "Weekly":
            totalGrossIncome += isWeekly
              ? rentAmountFloat
              : rentAmountFloat / WEEKS_PER_MONTH;
            break;
          case "Monthly":
            totalGrossIncome += isWeekly
              ? rentAmountFloat * WEEKS_PER_MONTH
              : rentAmountFloat;
            break;
          case "Yearly":
            totalGrossIncome += isWeekly
              ? rentAmountFloat * WEEKS_PER_YEAR
              : rentAmountFloat * MONTHS_PER_YEAR;
            break;
        }

        totalWeeklyIncome += isWeekly
          ? rentAmountFloat
          : rentAmountFloat / WEEKS_PER_MONTH;
      });

      setGrossIncome(totalGrossIncome); // Gross Income
      setResidentOwed(totalResidentOwed);

      const totalBills = getNetBillTotal();
      const netIncome = totalGrossIncome - totalBills; // Net Income after business expenses
      setNetIncome(netIncome);

      const yearlyNetIncome =
        selectedIncomeFilter === "Yearly"
          ? netIncome
          : selectedIncomeFilter === "Monthly"
          ? netIncome * MONTHS_PER_YEAR
          : netIncome * WEEKS_PER_YEAR;

      const yearlyTaxes = yearlyNetIncome * (taxPercentage / 100);
      const taxesOwed =
        selectedIncomeFilter === "Yearly"
          ? yearlyTaxes
          : selectedIncomeFilter === "Monthly"
          ? yearlyTaxes / MONTHS_PER_YEAR
          : yearlyTaxes / WEEKS_PER_YEAR;

      setTax((prevTax) => ({
        ...prevTax,
        amount: taxesOwed,
      }));

      const personalBills = getBillTotal("Personal");
      const netIncomeAfterTaxesAndBills = netIncome - taxesOwed - personalBills; // Net Income After Taxes and Bills
      setIncomeLessBills(netIncomeAfterTaxesAndBills);

      const weeklyNetIncome = totalWeeklyIncome - getNetBillTotal("Weekly");
      const hourlyWageCalc = weeklyNetIncome / hoursPerWeek;

      setHourlyWage(hourlyWageCalc);
    } catch (error) {
      console.error("Error calculating incomes:", error);
    }
  }, [
    organizationId,
    selectedIncomeFilter,
    allBills,
    getBillTotal,
    getNetBillTotal,
    taxPercentage,
    hoursPerWeek,
  ]);

  useEffect(() => {
    calculateIncome();
  }, [
    organizationId,
    allBills,
    selectedIncomeFilter,
    taxPercentage,
    hoursPerWeek,
  ]);

  const fetchSnapshots = useCallback(async () => {
    try {
      const snapshotsQuery = query(
        collection(db, `organizations/${organizationId}/snapshots`),
        orderBy("timestamp")
      );
      const snapshot = await getDocs(snapshotsQuery);
      if (!snapshot.empty) {
        const fetchedSnapshots = snapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp.toDate(),
        }));
        setSnapshots(fetchedSnapshots);
      }
    } catch (error) {
      console.error("Error fetching snapshot data:", error);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchSnapshots();
  }, [fetchSnapshots]);

  const submitBill = async () => {
    let billToSave = {
      ...currentBill,
      weeklyCost: 0,
      monthlyCost: 0,
      yearlyCost: 0,
    };

    const cost = parseFloat(currentBill.cost);
    switch (currentBill.schedule) {
      case "Weekly":
        billToSave.weeklyCost = cost;
        billToSave.monthlyCost = cost * WEEKS_PER_MONTH;
        billToSave.yearlyCost = cost * WEEKS_PER_YEAR;
        break;
      case "Monthly":
        billToSave.monthlyCost = cost;
        billToSave.weeklyCost = cost / WEEKS_PER_MONTH;
        billToSave.yearlyCost = cost * MONTHS_PER_YEAR;
        break;
      case "Yearly":
        billToSave.yearlyCost = cost;
        billToSave.weeklyCost = cost / WEEKS_PER_YEAR;
        billToSave.monthlyCost = cost / MONTHS_PER_YEAR;
        break;
    }

    try {
      const docRef = await addDoc(
        collection(db, "organizations", organizationId, "billsData"),
        billToSave
      );
      // Add the new bill to the local state immediately
      setAllBills((prevBills) => [
        ...prevBills,
        { id: docRef.id, ...billToSave },
      ]);

      // Close modal and reset the form
      setAddBillModalOpen(false);
      resetCurrentBill();
    } catch (error) {
      console.error("Error submitting bill:", error);
    }
  };

  const resetCurrentBill = () => {
    setCurrentBill({
      type: "Personal",
      name: "",
      cost: "",
      schedule: "Monthly",
    });
  };

  const deleteBillFromFirestore = async (billId) => {
    try {
      await deleteDoc(
        doc(db, `organizations/${organizationId}/billsData`, billId)
      );
      setAllBills((prevBills) =>
        prevBills.filter((bill) => bill.id !== billId)
      );
    } catch (error) {
      console.error("Error deleting bill:", error);
    }
  };

  const updateBillInFirestore = async (updatedBill) => {
    try {
      await updateDoc(
        doc(db, `organizations/${organizationId}/billsData`, updatedBill.id),
        updatedBill
      );
      setAllBills((prevBills) =>
        prevBills.map((bill) =>
          bill.id === updatedBill.id ? updatedBill : bill
        )
      );
    } catch (error) {
      console.error("Error updating bill:", error);
    }
  };

  const calculateCosts = (schedule, cost) => {
    const costNum = parseFloat(cost);
    switch (schedule) {
      case "Weekly":
        return {
          weeklyCost: costNum,
          monthlyCost: costNum * WEEKS_PER_MONTH,
          yearlyCost: costNum * WEEKS_PER_YEAR,
        };
      case "Monthly":
        return {
          monthlyCost: costNum,
          weeklyCost: costNum / WEEKS_PER_MONTH,
          yearlyCost: costNum * MONTHS_PER_YEAR,
        };
      case "Yearly":
        return {
          yearlyCost: costNum,
          weeklyCost: costNum / WEEKS_PER_YEAR,
          monthlyCost: costNum / MONTHS_PER_YEAR,
        };
      default:
        return { weeklyCost: 0, monthlyCost: 0, yearlyCost: 0 };
    }
  };

  useEffect(() => {
    const fetchPayments = async () => {
      if (!organizationId) return;

      try {
        // Get all unpaid invoices from the centralized invoices collection
        const invoicesQuery = query(
          collection(db, `organizations/${organizationId}/invoices`),
          where("paidInvoice", "==", false)
        );

        const invoicesSnapshot = await getDocs(invoicesQuery);

        // Calculate total owed from all unpaid invoices
        const totalOwed = invoicesSnapshot.docs.reduce((sum, doc) => {
          return sum + parseFloat(doc.data().paymentAmount || 0);
        }, 0);

        setInvoicesOwed(totalOwed);
      } catch (error) {
        console.error("Error fetching invoices:", error);
      }
    };

    fetchPayments();
  }, [organizationId]);

  useEffect(() => {
    const calculateTax = () => {
      const yearlyNetIncome =
        selectedIncomeFilter === "Yearly"
          ? netIncome
          : selectedIncomeFilter === "Monthly"
          ? netIncome * MONTHS_PER_YEAR
          : netIncome * WEEKS_PER_YEAR;

      const yearlyTaxAmount = yearlyNetIncome * TAX_RATE;

      const taxAmount =
        selectedIncomeFilter === "Yearly"
          ? yearlyTaxAmount
          : selectedIncomeFilter === "Monthly"
          ? yearlyTaxAmount / MONTHS_PER_YEAR
          : yearlyTaxAmount / WEEKS_PER_YEAR;

      setTax({
        rate: `${(TAX_RATE * 100).toFixed(0)}%`,
        amount: taxAmount,
        weekly: yearlyTaxAmount / WEEKS_PER_YEAR,
        monthly: yearlyTaxAmount / MONTHS_PER_YEAR,
        yearly: yearlyTaxAmount,
      });
    };

    calculateTax();
  }, [selectedIncomeFilter, netIncome]);

  const handleIncomeFilterChange = (event) => {
    setSelectedIncomeFilter(event.target.value);
  };

  const handleNotificationSubmit = async (
    subject,
    note,
    recipients,
    notificationType = "manual",
    forceEmail = false
  ) => {
    console.log("Starting notification submission with:", {
      subject,
      note,
      recipients,
      notificationType,
      forceEmail,
    });

    if (!recipients?.length) {
      alert("Please select at least one recipient.");
      return;
    }

    const roles = ["All", "Admin", "Staff", "Leader", "Resident"];
    const selectedRoles = recipients.filter((id) => roles.includes(id));
    const individualUsers = recipients.filter((id) => !roles.includes(id));

    console.log("Selected roles:", selectedRoles);
    console.log("Individual users:", individualUsers);

    try {
      // Create batch to handle multiple writes
      const batch = writeBatch(db);

      // Step 1: If there are role-based recipients, find all users with those roles
      let targetUsers = [...individualUsers]; // Start with directly selected users
      console.log("Initial target users:", targetUsers);

      if (selectedRoles.length > 0) {
        console.log(`Querying users in organization: ${organizationId}`);
        // Query all users in this organization
        const usersSnapshot = await getDocs(
          collection(db, `organizations/${organizationId}/users`)
        );

        console.log(`Found ${usersSnapshot.size} users in organization`);

        // Filter users whose roles match the selected roles
        // Also add "All" recipients to everyone
        usersSnapshot.forEach((userDoc) => {
          const userData = userDoc.data();
          console.log(
            `Checking user ${userDoc.id} with role: ${userData.role}`
          );

          if (
            selectedRoles.includes(userData.role) ||
            selectedRoles.includes("All")
          ) {
            // Add user if not already in the list
            if (!targetUsers.includes(userDoc.id)) {
              console.log(
                `Adding user ${userDoc.id} with role ${userData.role} to recipients`
              );
              targetUsers.push(userDoc.id);
            }
          }
        });
      }

      console.log("Final target users:", targetUsers);

      // Step 2: Create individual notification for each user
      const notificationData = {
        subject,
        note,
        date: Timestamp.now(),
        isRead: false,
        createdBy: user.email || "System",
        sourceType: notificationType, // Include the notification type
        createdAt: serverTimestamp(),
        forceEmail: forceEmail, // Flag to override user email preferences if true
      };

      console.log("Notification data:", notificationData);

      // Add a notification document to each user's subcollection
      for (const userId of targetUsers) {
        const notificationPath = `organizations/${organizationId}/users/${userId}/notifications`;
        console.log(`Creating notification at path: ${notificationPath}`);

        const notificationRef = doc(collection(db, notificationPath));

        console.log(`Notification document ID: ${notificationRef.id}`);
        batch.set(notificationRef, notificationData);
      }

      // Commit all the writes
      console.log("Committing batch write...");
      await batch.commit();
      console.log(
        `Successfully created notifications for ${targetUsers.length} users`
      );
    } catch (error) {
      console.error("Failed to send notification:", error);
      console.error("Error details:", error.message, error.code);
    }
  };

  const handleTaxChange = async (value) => {
    setTaxPercentage(value);
    try {
      await updateDoc(
        doc(db, `organizations/${organizationId}/houseSettings/initial`),
        {
          taxPercentage: value,
        }
      );
      await calculateIncome(); // Trigger recalculation
    } catch (error) {
      console.error("Error updating tax percentage:", error);
    }
  };

  const handleHoursChange = async (value) => {
    setHoursPerWeek(value);
    try {
      await updateDoc(
        doc(db, `organizations/${organizationId}/houseSettings/initial`),
        {
          hoursPerWeek: value,
        }
      );
      await calculateIncome(); // Trigger recalculation
    } catch (error) {
      console.error("Error updating hours per week:", error);
    }
  };

  const handleFileUpload = async () => {
    if (!fileName.trim()) {
      setHasAttemptedSubmit(true);
      setIsShaking(true);
      setTimeout(() => setIsShaking(false), 650);
      return;
    }

    if (selectedFiles.length === 0) return;

    try {
      const uploadPromises = selectedFiles.map(async (file) => {
        // Simplify storage path while keeping files organized
        const storageRef = ref(
          storage,
          `uploads/${organizationId}/${file.name}`
        );

        // Upload file
        const snapshot = await uploadBytes(storageRef, file);
        const fileURL = await getDownloadURL(snapshot.ref);

        // Keep the organization-based structure for Firestore
        await addDoc(
          collection(db, `organizations/${organizationId}/importantDocuments`),
          {
            fileName: fileName.trim(),
            originalFileName: file.name,
            fileURL,
            createdAt: new Date(),
            uploadedBy: auth.currentUser?.email,
            path: `uploads/${organizationId}/${file.name}`, // Store path for reference
          }
        );
      });

      await Promise.all(uploadPromises);
      setSelectedFiles([]);
      setFileName("");
      setDocumentModal(false);
      setHasAttemptedSubmit(false);
    } catch (error) {
      console.error("Error uploading files:", error);
      // More detailed error for debugging
      console.error("Detailed error:", error.message);
      alert(`Error uploading files: ${error.message}`);
    }
  };

  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    setSelectedFiles((prev) => [...prev, ...newFiles]);
    setDocumentModal(true);
    setHasAttemptedSubmit(false); // Reset validation state when opening modal
  };

  const handleFileNameChange = (event) => {
    setFileName(event.target.value);
    if (hasAttemptedSubmit) {
      setHasAttemptedSubmit(false);
    }
  };

  const removeFile = (indexToRemove) => {
    setSelectedFiles((prev) =>
      prev.filter((_, index) => index !== indexToRemove)
    );
  };

  const closeModal = () => {
    setDocumentModal(false);
    setHasAttemptedSubmit(false);
    setFileName("");
    setSelectedFiles([]);
  };

  const handleOpenNotificationModal = () => {
    setNotificationModalOpen(true);
  };

  const handleCloseNotificationModal = () => {
    setNotificationModalOpen(false);
  };

  const handleSaveSnapshot = () => {
    setIsSaveSnapshotModalOpen(true);
  };

  const handleSaveSnapshotData = async () => {
    const personalBills = getBillTotal("Personal");
    const snapshotData = {
      grossIncome,
      netIncome,
      incomeLessBills,
      bills: Object.fromEntries(
        Object.keys(housesData).map((house) => [house, getBillTotal(house)])
      ),
      personalBills, // Add personal bills to the snapshot data
      timestamp: new Date(),
    };

    try {
      await addDoc(
        collection(db, `organizations/${organizationId}/snapshots`),
        snapshotData
      );
      console.log("Snapshot saved successfully!");
      setIsSaveSnapshotModalOpen(false);
    } catch (error) {
      console.error("Error saving snapshot:", error);
    }
  };

  const incomeFilterOptions = useMemo(
    () => (
      <>
        <option value="Weekly">Weekly</option>
        <option value="Monthly">Monthly</option>
        <option value="Yearly">Yearly</option>
      </>
    ),
    []
  );

  if (isLoading) {
    return <Spinner />;
  }

  if (!checkPermission("accessOwnerDashboard")) {
    return (
      <div className="flex items-center justify-center min-h-screen text-3xl font-bold">
        Access Denied
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>&bull; Owner Overview</title>
      </Helmet>

      <div className="min-h-screen bg-[#18181B] bg-gradient-to-br from-zinc-900 via-zinc-900/95 to-zinc-950 p-6 relative overflow-hidden">
        {/* Ambient background effects */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,24,27,0.3),rgba(0,0,0,0))]" />
        <div className="absolute inset-0">
          <div className="absolute top-0 -left-4 w-96 h-96 bg-cyan-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-500/10 rounded-full mix-blend-screen filter blur-3xl animate-pulse delay-700" />
        </div>

        <div className="max-w-7xl mx-auto space-y-8 relative">
          {/* Header */}
          <div className="flex items-center gap-6 mb-12">
            <div className="flex gap-3">
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-100 opacity-70" />
              <div className="h-16 w-1 bg-gradient-to-b from-cyan-500 to-cyan-600 rounded-full animate-pulse delay-200 opacity-40" />
            </div>
            <div>
              <h1 className="text-4xl font-bold text-white tracking-tight">
                Admin Overview
              </h1>
            </div>
          </div>

          {/* Action Buttons and Filter */}
          <div className="flex flex-col md:flex-row justify-between gap-4">
            {/* Buttons on the left */}
            <div className="flex flex-wrap gap-4">
              <button
                onClick={handleSaveSnapshot}
                className="px-4 py-2 bg-blue-500/30 hover:bg-blue-500/40 text-blue-300 hover:text-blue-200 rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto"
              >
                <Save size={20} />
                <span>Save Data</span>
              </button>
              <button
                onClick={() => setDocumentModal(true)}
                className="px-4 py-2 bg-purple-500/30 hover:bg-purple-500/40 text-purple-300 hover:text-purple-200 rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto"
              >
                <FilePlus2 size={20} />
                <span>Add Document</span>
              </button>
              <button
                onClick={handleOpenNotificationModal}
                className="px-4 py-2 bg-green-500/30 hover:bg-green-500/40 text-green-300 hover:text-green-200 rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto"
              >
                <BellPlus size={20} />
                <span>Send Notification</span>
              </button>
              <button
                onClick={() => setAddBillModalOpen(true)}
                className="px-4 py-2 bg-red-500/30 hover:bg-red-500/40 text-red-300 hover:text-red-200 rounded-lg transition-colors flex items-center gap-2 w-full sm:w-auto"
              >
                <Plus size={20} />
                <span>Add Bill</span>
              </button>
            </div>

            {/* Filter on the right */}
            <div className="flex items-center">
              <select
                value={selectedIncomeFilter}
                onChange={handleIncomeFilterChange}
                className="px-4 py-2 bg-slate-800/70 rounded-lg border border-slate-700 text-white font-medium hover:bg-slate-800/90 focus:ring-2 focus:ring-blue-500 w-full sm:w-auto"
              >
                {incomeFilterOptions}
              </select>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative overflow-visible">
            {/* Gross Income */}
            <StatCard
              title="Gross Income"
              value={formatCurrency(grossIncome)}
              icon={CircleDollarSign}
              color="emerald"
              tooltip="Total income generated by all residents based on their rent schedules."
            />

            <StatCard
              title="Net Income"
              value={formatCurrency(netIncome)}
              icon={TrendingUp}
              color="blue"
              tooltip="Gross income minus all business expenses."
            />

            <StatCard
              title="Net Income After Taxes and Bills"
              value={formatCurrency(incomeLessBills)}
              icon={Wallet}
              color="purple"
              tooltip="Net income after accounting for taxes and personal bills. This is what you have left after everything."
            />

            <StatCard
              title="Taxes Owed"
              value={formatCurrency(tax.amount)}
              icon={Percent}
              color="green"
              tooltip="Taxes calculated as a percentage of net income. Adjust using the input below."
            >
              <div className="absolute bottom-4 right-4">
                <input
                  type="number"
                  value={taxPercentage}
                  onChange={(e) => handleTaxChange(parseFloat(e.target.value))}
                  className="w-16 px-2 py-1 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:ring-2 ring-green-400"
                  min="0"
                  max="100"
                />
              </div>
            </StatCard>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 relative overflow-visible">
            {/* Total Bills */}
            <StatCard
              title="Total Bills"
              value={formatCurrency(
                getBillTotal("Personal") +
                  Object.keys(housesData).reduce(
                    (acc, house) => acc + getBillTotal(house),
                    0
                  )
              )}
              icon={TrendingDown}
              color="red"
              isNegative
              tooltip="Sum of all active bills, including house-related and personal bills."
            />

            <StatCard
              title="Invoices Owed"
              value={formatCurrency(invoicesOwed)}
              icon={ClipboardList}
              color="red"
              isNegative
              tooltip="Total unpaid invoices from residents or other sources."
            />

            {/* Resident Owed */}
            <StatCard
              title="Resident Owed"
              value={formatCurrency(residentOwed)}
              icon={Users}
              color="red"
              isNegative
              tooltip="Total outstanding balances owed by residents."
            />

            {/* Hourly Wage */}
            <StatCard
              title="Hourly Wage"
              value={formatCurrency(hourlyWage)}
              icon={Clock}
              color="blue"
              tooltip="Hourly income dynamically calculated based on weekly net business income (excluding owner salary or distributions) divided by configured hours worked per week. Reflects changes in census, income, and expenses in real time."
            >
              {/* Hours Adjustment Input */}
              <div className="absolute bottom-4 right-4">
                <input
                  type="number"
                  value={hoursPerWeek}
                  onChange={(e) =>
                    handleHoursChange(parseFloat(e.target.value))
                  }
                  className="w-16 px-2 py-1 bg-slate-800 border border-slate-700 rounded-lg text-sm text-white focus:ring-2 ring-blue-400"
                  min="1"
                  max="168"
                />
              </div>
            </StatCard>
          </div>

          {/* Bills Sections */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
            {/* Personal Bills Section */}
            <BillSection
              key="Personal"
              title="Personal"
              isOpen={openHouseSections["Personal"] || false}
              toggle={() =>
                setOpenHouseSections((prev) => ({
                  ...prev,
                  Personal: !prev.Personal,
                }))
              }
              bills={allBills.filter((bill) => bill.type === "Personal")}
              getBillTotal={getBillTotal}
              selectedIncomeFilter={selectedIncomeFilter}
              onEditBill={(bill) => {
                setBillToEdit(bill);
                setEditModalOpen(true);
              }}
              icon={User} // Use a different icon if needed
              accentColor="emerald"
            />

            {/* House-Specific Bill Sections */}
            {Object.entries(housesData).map(([houseName, houseData]) => (
              <BillSection
                key={houseName}
                title={`${houseName}`}
                isOpen={openHouseSections[houseName] || false}
                toggle={() =>
                  setOpenHouseSections((prev) => ({
                    ...prev,
                    [houseName]: !prev[houseName],
                  }))
                }
                bills={allBills.filter((bill) => bill.type === houseName)}
                getBillTotal={getBillTotal}
                selectedIncomeFilter={selectedIncomeFilter}
                onEditBill={(bill) => {
                  setBillToEdit(bill);
                  setEditModalOpen(true);
                }}
                icon={Home}
                accentColor={houseData.color}
              />
            ))}
          </div>

          {/* Chart Section */}
          <div className="relative bg-slate-900/50 backdrop-blur-xl rounded-xl overflow-hidden hover:shadow-2xl transition-all duration-500">
            <div className="w-full overflow-x-auto">
              <ResponsiveContainer width="100%" height={500} minWidth={300}>
                <LineChart
                  data={snapshots}
                  margin={{ top: 15, right: 15, left: 50, bottom: 15 }} // Increase left margin to 50 or adjust as needed
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="timestamp" stroke="#9CA3AF" tick={false} />

                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    content={<CustomTooltip housesData={housesData} />}
                  />
                  <Legend />
                  {Object.entries(housesData).map(([house, data]) => (
                    <Line
                      key={house}
                      type="monotone"
                      dataKey={`bills.${house}`}
                      strokeWidth={3}
                      stroke={data.color}
                      name={house}
                      dot={false}
                    />
                  ))}
                  <Line
                    type="monotone"
                    dataKey="grossIncome"
                    strokeWidth={3}
                    stroke="#60A5FA"
                    name="Gross Income"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="netIncome"
                    strokeWidth={3}
                    stroke="#8B5CF6"
                    name="Net Income"
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="incomeLessBills"
                    strokeWidth={3}
                    stroke="#EC4899"
                    name="Income Less Bills"
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>
      <Modal
        isOpen={addBillModalOpen}
        onClose={() => setAddBillModalOpen(false)}
        title="Add Bill"
      >
        <div className="space-y-4 p-4">
          <div>
            <label className="block text-white mb-2">Type</label>
            <select
              value={currentBill.type}
              onChange={(e) =>
                setCurrentBill({ ...currentBill, type: e.target.value })
              }
              className="w-full px-3 py-2 text-white border rounded-lg focus:ring-2 ring-slate-600 bg-slate-800/50"
            >
              <option value="Personal">Personal</option>
              {Object.keys(housesData).map((house) => (
                <option key={house} value={house}>
                  {house}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-white mb-2">Schedule</label>
            <select
              value={currentBill.schedule}
              onChange={(e) =>
                setCurrentBill({ ...currentBill, schedule: e.target.value })
              }
              className="w-full px-3 py-2 text-white border rounded-lg focus:ring-2 ring-slate-600 bg-slate-800/50"
            >
              <option value="Weekly">Weekly</option>
              <option value="Monthly">Monthly</option>
              <option value="Yearly">Yearly</option>
            </select>
          </div>

          <div>
            <label className="block text-white mb-2">Name</label>
            <input
              type="text"
              value={currentBill.name}
              onChange={(e) =>
                setCurrentBill({ ...currentBill, name: e.target.value })
              }
              className="w-full px-3 py-2 text-white border rounded-lg focus:ring-2 ring-slate-600 bg-slate-800/50"
              placeholder="Enter bill name"
            />
          </div>

          <div>
            <label className="block text-white mb-2">Cost</label>
            <input
              type="number"
              value={currentBill.cost}
              onChange={(e) =>
                setCurrentBill({ ...currentBill, cost: e.target.value })
              }
              className="w-full px-3 py-2 text-white border rounded-lg focus:ring-2 ring-slate-600 bg-slate-800/50"
              placeholder="Enter cost"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-700">
          <button
            onClick={() => {
              setAddBillModalOpen(false);
              resetCurrentBill();
            }}
            className="px-4 py-2 text-red-400 hover:text-red-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              submitBill();
              setAddBillModalOpen(false);
              resetCurrentBill();
            }}
            className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-all"
          >
            Submit
          </button>
        </div>
      </Modal>
      <Modal
        isOpen={isEditModalOpen && billToEdit !== null}
        onClose={() => setEditModalOpen(false)}
        title="Edit Bill"
      >
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (billToEdit) {
              await updateBillInFirestore(billToEdit);
              setEditModalOpen(false);
            }
          }}
        >
          <div className="space-y-4">
            <input
              type="text"
              value={billToEdit?.name || ""}
              onChange={(e) =>
                setBillToEdit({ ...billToEdit, name: e.target.value })
              }
              className="w-full px-3 py-2 text-white text-sm border rounded-lg focus:ring-2 ring-slate-600 bg-slate-800/50"
            />
            <select
              value={billToEdit?.schedule || ""}
              onChange={(e) => {
                const newSchedule = e.target.value;
                setBillToEdit((prevBill) => ({
                  ...prevBill,
                  schedule: newSchedule,
                  ...calculateCosts(newSchedule, prevBill?.cost || 0),
                }));
              }}
              className="w-full px-3 py-2 text-white text-sm border rounded-lg focus:ring-2 ring-slate-600 bg-slate-800/50"
            >
              <option>Weekly</option>
              <option>Monthly</option>
              <option>Yearly</option>
            </select>
            <input
              type="number"
              value={billToEdit?.cost || 0}
              onChange={(e) => {
                const newCost = parseFloat(e.target.value);
                setBillToEdit((prevBill) => ({
                  ...prevBill,
                  cost: newCost,
                  ...calculateCosts(prevBill?.schedule || "Monthly", newCost),
                }));
              }}
              className="w-full px-3 py-2 text-white text-sm border rounded-lg focus:ring-2 ring-slate-600 bg-slate-800/50"
            />
            <div className="flex justify-center space-x-4 pt-4">
              <button
                type="button"
                onClick={() => {
                  deleteBillFromFirestore(billToEdit.id);
                  setEditModalOpen(false);
                }}
                className="px-4 py-2 bg-red-500/10 text-red-400 rounded-lg hover:bg-red-500/20 transition-all"
              >
                Delete
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-all"
              >
                Save Changes
              </button>
            </div>
          </div>
        </form>
      </Modal>

      <Modal
        isOpen={isSaveSnapshotModalOpen}
        onClose={() => setIsSaveSnapshotModalOpen(false)}
        title="Save Snapshot"
      >
        <div className="space-y-4 p-4">
          <p className="text-white text-lg">
            Are you sure you want to save this data?
          </p>
          <div className="text-slate-300 space-y-2">
            <p>
              This will create a new snapshot with the following information:
            </p>
            <ul className="list-disc pl-5 space-y-1">
              <li>Current gross income</li>
              <li>Net income</li>
              <li>Income less bills</li>
              <li>Bills breakdown by house</li>
            </ul>
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-700">
          <button
            onClick={() => setIsSaveSnapshotModalOpen(false)}
            className="px-4 py-2 text-red-400 hover:text-red-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSaveSnapshotData}
            className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-all"
          >
            Save
          </button>
        </div>
      </Modal>

      <NotificationModal
        users={users}
        isOpen={notificationModalOpen}
        onClose={() => setNotificationModalOpen(false)}
        onSubmit={handleNotificationSubmit}
        Modal={Modal} // Pass the locally defined Modal
      />

      <input
        type="file"
        id="fileInput"
        onChange={handleFileChange}
        className="hidden"
      />

      <Modal
        isOpen={documentModal}
        onClose={() => {
          closeModal();
          setSelectedFiles([]);
        }}
        title="Add Documents"
        className="fixed inset-0 flex items-center justify-center overflow-hidden"
      >
        <div className="space-y-4 p-4">
          <div>
            <label className="block text-white mb-2">Group Name</label>
            <input
              type="text"
              placeholder="Enter group name"
              value={fileName}
              onChange={handleFileNameChange}
              className={`w-full px-3 py-2 text-white border rounded-lg focus:ring-2 ring-slate-600 bg-slate-800/50 ${
                hasAttemptedSubmit && !fileName.trim()
                  ? "border-red-500 animate-shake"
                  : "border-slate-600"
              }`}
            />
            {hasAttemptedSubmit && !fileName.trim() && (
              <p className="mt-2 text-sm text-red-400">
                Please enter a group name
              </p>
            )}
          </div>

          {/* Selected Files List */}
          {selectedFiles.length > 0 && (
            <div className="mt-4 space-y-2 max-h-48 overflow-y-auto">
              {selectedFiles.map((file, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between bg-slate-800/50 p-2 rounded-lg"
                >
                  <span className="text-slate-300 truncate">{file.name}</span>
                  <button
                    onClick={() => removeFile(index)}
                    className="p-1 hover:bg-slate-700 rounded-lg text-red-400 hover:text-red-300"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* File Input Button */}
          <input
            type="file"
            id="fileInput"
            hidden
            multiple
            onChange={handleFileChange}
          />
          <button
            onClick={() => document.getElementById("fileInput").click()}
            className="px-4 py-2 bg-slate-700 text-slate-300 hover:bg-slate-600 hover:text-white rounded-lg flex items-center gap-2"
          >
            <Plus size={20} />
            Add Files
          </button>
        </div>

        <div className="flex items-center justify-end gap-3 p-4 border-t border-slate-700">
          <button
            onClick={() => {
              closeModal();
              setSelectedFiles([]);
            }}
            className="px-4 py-2 text-slate-400 hover:text-slate-300 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleFileUpload}
            className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg hover:bg-blue-500/20 transition-all"
          >
            Upload Files
          </button>
        </div>
      </Modal>
    </>
  );
};

export default Owner;
